// Build Campaign Phase 2 — the pure planner that turns the rollout's three Drive
// folders (live listings + the snapshot AssetLabel) into a capped, ordered list of
// ads to seed onto the draft. No I/O — the caller lists the folders, runs this, then
// streams + uploads the kept assets. See docs/build-campaign-phase2-spec.md.
//
// The LABEL is the key. AssetLabel carries TWO fields: `adType` ('feed' | 'story',
// the placement) and `label` (the creative-concept name). Matching + ad naming key
// on `label`; the FILENAME is only a fallback tiebreaker.
//
// Rules (WITHIN a folder, so each ad belongs to one folder for the cap):
//   - Classify each media file by AssetLabel.adType (placement authority) + MIME.
//     No adType → skip + flag.
//   - GROUP by `label`. A clean group (one feed + one story by adType, same media) →
//     PAIR; the ad name is the label. Mixed media in a clean pair → flag, don't split.
//   - DUPLICATE-LABEL group (multiple feeds and/or stories): the label can't say which
//     feed pairs with which story → fall back to the trimmed FILENAME base (FBA/FBS
//     stripped) to pair within the group; same media type; leftovers → singles + flag.
//     Identical label AND filename (indistinguishable) → flag, all → singles.
//   - SINGLE (group with only feed or only story, or a leftover) → ONE ad; the caller
//     puts the one asset in BOTH slots so it runs across all placements (not dropped
//     from stories). Ad name = the label.
//   - NO label (adType set, `label` empty) → group by filename, name from the filename
//     + flag "set a label".
//   - 10-ad cap: drop by FOLDER ORDER — National first, then Local, then Organic.

import type { AssetLabel } from '../../types/campaign';

export const MAX_ADS = 10;

export type DriveFolderKey = 'national' | 'local' | 'organic';

// The folders in cap drop-order: National dropped first, Organic last (most
// protected).
export const FOLDER_ORDER: DriveFolderKey[] = ['national', 'local', 'organic'];

export const FOLDER_LABEL: Record<DriveFolderKey, string> = {
  national: 'National',
  local: 'Local',
  organic: 'Organic',
};

export type PlannerFile = {
  id: string;
  name: string;
  mimeType: string;
};

export type PlannerFolderInput = {
  key: DriveFolderKey;
  files: PlannerFile[];
  labels: AssetLabel[];
};

export type PlannedAsset = {
  fileId: string;
  fileName: string;
  mediaType: 'image' | 'video';
};

export type PlannedAd = {
  folder: DriveFolderKey;
  kind: 'pair' | 'single';
  mediaType: 'image' | 'video';
  name: string; // ad name = the AssetLabel.label (or the filename when unlabelled)
  feed: PlannedAsset; // feed asset (pair) OR the single asset (used for both slots)
  story: PlannedAsset | null; // story asset (pair); null for a single
};

export type DriveCreativePlan = {
  ads: PlannedAd[]; // ≤ MAX_ADS, in folder order (National → Local → Organic)
  warnings: string[];
  counts: { found: number; added: number; dropped: number };
};

// MIME → media type. null = not a usable creative file (skipped silently).
export function mediaTypeOf(mimeType: string): 'image' | 'video' | null {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  return null;
}

// Strip the extension + any FBA/FBS token (bounded by non-letters), then trim. The
// FBA/FBS in the filename is cosmetic; this only derives the PAIRING key — the
// authoritative feed/story split is AssetLabel.adType. Case-insensitive token match;
// leading/trailing whitespace trimmed (guards a stray space).
export function deriveBaseName(name: string): string {
  const noExt = name.replace(/\.[^.]+$/, '');
  const stripped = noExt.replace(/(^|[^a-zA-Z])(fba|fbs)(?=[^a-zA-Z]|$)/gi, '$1');
  return stripped.trim();
}

// Normalize an AssetLabel.label into the clean creative-concept name used BOTH as the
// LABEL grouping key and as the ad name. Real labels carry an FBA/FBS placement prefix
// (feed = "FBA - National Offer Primary", story = "FBS - National Offer Primary"), so
// grouping on the raw label splits a feed+story concept into two singletons that never
// pair. Stripping the bounded FBA/FBS token (case-insensitive) collapses both to
// "National Offer Primary" → one group → resolveGroup Case A pairs them. Also collapses
// doubled internal whitespace and trims surrounding separators (`-`, `–`, `:`, spaces).
// A label with no FBA/FBS token is returned cleaned/unchanged.
export function normalizeLabel(label: string): string {
  return label
    .replace(/(^|[^a-zA-Z])(fba|fbs)(?=[^a-zA-Z]|$)/gi, '$1')
    .replace(/\s+/g, ' ')
    .replace(/^[\s\-–:]+|[\s\-–:]+$/g, '')
    .trim();
}

type Classified = {
  file: PlannerFile;
  mediaType: 'image' | 'video';
  adType: 'feed' | 'story';
  label: string; // AssetLabel.label, trimmed ('' when unlabelled)
  baseName: string; // filename fallback key
};

function asset(c: Classified): PlannedAsset {
  return { fileId: c.file.id, fileName: c.file.name, mediaType: c.mediaType };
}

function pairUnit(folder: DriveFolderKey, name: string, f: Classified, s: Classified): PlannedAd {
  return { folder, kind: 'pair', mediaType: f.mediaType, name, feed: asset(f), story: asset(s) };
}
function singleUnit(folder: DriveFolderKey, name: string, c: Classified): PlannedAd {
  return { folder, kind: 'single', mediaType: c.mediaType, name, feed: asset(c), story: null };
}

// Resolve ONE group (all assets sharing a key — a label, or a filename base for the
// unlabelled path) into ad units, all named `name`. Three layers: clean one-feed-one-
// story pair → duplicate-key filename fallback → singles.
function resolveGroup(
  group: Classified[],
  name: string,
  folder: DriveFolderKey,
  warnings: string[],
): PlannedAd[] {
  const feeds = group.filter((c) => c.adType === 'feed');
  const stories = group.filter((c) => c.adType === 'story');
  const units: PlannedAd[] = [];

  // CASE A — clean pair: exactly one feed + one story.
  if (feeds.length === 1 && stories.length === 1) {
    const f = feeds[0]!;
    const s = stories[0]!;
    if (f.mediaType === s.mediaType) {
      units.push(pairUnit(folder, name, f, s));
    } else {
      // Meta can't run a video on one placement + an image on another. Don't split.
      warnings.push(
        `"${name}" has a feed ${f.mediaType} and a story ${s.mediaType} — can't pair mixed media; review the labels.`,
      );
    }
    return units;
  }

  // CASE B — only one placement present → each is a single.
  if (feeds.length === 0 || stories.length === 0) {
    for (const c of group) units.push(singleUnit(folder, name, c));
    return units;
  }

  // CASE C — duplicate key with BOTH feeds + stories: the key can't disambiguate which
  // feed pairs with which story. Fall back to the FILENAME base within the group.
  const storyByBase = new Map<string, Classified[]>();
  for (const s of stories) {
    if (!storyByBase.has(s.baseName)) storyByBase.set(s.baseName, []);
    storyByBase.get(s.baseName)!.push(s);
  }
  const feedCountByBase = new Map<string, number>();
  for (const f of feeds) feedCountByBase.set(f.baseName, (feedCountByBase.get(f.baseName) ?? 0) + 1);

  const usedStories = new Set<Classified>();
  let identical = false;
  let paired = 0;
  for (const f of feeds) {
    const sameBaseStories = storyByBase.get(f.baseName) ?? [];
    const ambiguous = (feedCountByBase.get(f.baseName) ?? 0) > 1 || sameBaseStories.length > 1;
    const match = ambiguous ? undefined : sameBaseStories.find((s) => !usedStories.has(s) && s.mediaType === f.mediaType);
    if (match) {
      units.push(pairUnit(folder, name, f, match));
      usedStories.add(match);
      paired += 1;
    } else {
      if (ambiguous) identical = true;
      units.push(singleUnit(folder, name, f)); // leftover feed → single
    }
  }
  for (const s of stories) {
    if (!usedStories.has(s)) units.push(singleUnit(folder, name, s)); // leftover story → single
  }

  const leftover = feeds.length + stories.length - paired * 2;
  if (identical) {
    warnings.push(
      `"${name}" has multiple assets with the same label and filename — can't determine pairing; review (added as singles).`,
    );
  } else if (leftover > 0) {
    warnings.push(
      `"${name}" has ${feeds.length} feed and ${stories.length} story assets — paired ${paired} by filename; ${leftover} added as singles.`,
    );
  }
  return units;
}

// Build the ad units for ONE folder (no cap applied here). Grouping is within-folder
// so every unit belongs to exactly one folder (the cap drops by folder).
function planFolder(input: PlannerFolderInput, warnings: string[]): PlannedAd[] {
  const labelById = new Map<string, AssetLabel>();
  for (const l of input.labels) labelById.set(l.driveFileId, l);

  // Classify: keep only media files that carry an authoritative feed/story placement.
  const labelled: Classified[] = []; // has a concept label → key on it
  const unlabelled: Classified[] = []; // adType set but no concept label → key on filename
  for (const file of input.files) {
    const media = mediaTypeOf(file.mimeType);
    if (!media) continue; // non-image/video file — not creative, skip.
    const entry = labelById.get(file.id);
    const adType = entry?.adType ?? null;
    if (adType !== 'feed' && adType !== 'story') {
      warnings.push(`"${file.name}" has no feed/story label — set one in the rollout.`);
      continue;
    }
    const label = (entry?.label ?? '').trim();
    const c: Classified = { file, mediaType: media, adType, label, baseName: deriveBaseName(file.name) };
    if (label) labelled.push(c);
    else {
      warnings.push(`"${file.name}" has no label — the ad is named from the filename; set a label in the rollout.`);
      unlabelled.push(c);
    }
  }

  const units: PlannedAd[] = [];

  // Labelled → group by the NORMALIZED label (FBA/FBS stripped, first-seen order) so a
  // feed "FBA - X" and a story "FBS - X" land in one group; name each unit the
  // normalized label.
  const byLabel = new Map<string, Classified[]>();
  const labelOrder: string[] = [];
  for (const c of labelled) {
    const key = normalizeLabel(c.label);
    if (!byLabel.has(key)) {
      byLabel.set(key, []);
      labelOrder.push(key);
    }
    byLabel.get(key)!.push(c);
  }
  for (const label of labelOrder) {
    units.push(...resolveGroup(byLabel.get(label)!, label, input.key, warnings));
  }

  // Unlabelled → group by filename base, name each unit the base.
  const byBase = new Map<string, Classified[]>();
  const baseOrder: string[] = [];
  for (const c of unlabelled) {
    if (!byBase.has(c.baseName)) {
      byBase.set(c.baseName, []);
      baseOrder.push(c.baseName);
    }
    byBase.get(c.baseName)!.push(c);
  }
  for (const base of baseOrder) {
    units.push(...resolveGroup(byBase.get(base)!, base || 'Untitled', input.key, warnings));
  }

  return units;
}

export function planDriveCreative(folders: PlannerFolderInput[]): DriveCreativePlan {
  const warnings: string[] = [];

  // Build units folder-by-folder in cap drop-order so slicing the front drops
  // National first, then Local, then Organic.
  const byKey = new Map(folders.map((f) => [f.key, f]));
  const ordered: PlannedAd[] = [];
  for (const key of FOLDER_ORDER) {
    const folder = byKey.get(key);
    if (folder) ordered.push(...planFolder(folder, warnings));
  }

  const found = ordered.length;
  if (found === 0) {
    warnings.push('No labelled creative found in the Drive folders — ads stay placeholder.');
    return { ads: [], warnings, counts: { found: 0, added: 0, dropped: 0 } };
  }

  // 10-ad cap. Drop from the FRONT (National first), keeping the last MAX_ADS.
  const dropped = Math.max(0, found - MAX_ADS);
  const ads = dropped > 0 ? ordered.slice(dropped) : ordered;
  if (dropped > 0) {
    warnings.push(
      `${found} found, ${ads.length} added, ${dropped} dropped — Meta's limit is 10 ads per campaign (dropped National first, then Local, then Organic).`,
    );
  }

  return { ads, warnings, counts: { found, added: ads.length, dropped } };
}
